import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogHeader, 
  DialogTitle 
} from '../ui/dialog';
import { Search, Plus, Edit, CheckCircle, XCircle, Loader2, AlertCircle, Trash2, Copy } from 'lucide-react';
import { apiService, LLMModel, LLMProvider, CreateModelRequest, UpdateModelRequest } from '../../services/api';
import { ParameterEditor } from '../ParameterEditor';
import { ParameterPreview } from '../ParameterPreview';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';


/** Leeres Formular für ein neues Modell — auch beim Öffnen von „Neues Modell“, damit keine Werte eines zuvor bearbeiteten Modells hängen bleiben. */
const emptyModelForm: CreateModelRequest = {
  providerId: '',
  modelKey: '',
  displayName: '',
  description: '',
  supportsTools: false,
  supportsVision: false,
  supportsStreaming: false,
  supportsSystemMessages: false,
  maxTokens: 4096,
  contextWindow: 16384,
  inputCostPerMillionTokensEur: 1,
  outputCostPerMillionTokensEur: 2,
  cachedInputCostPerMillionTokensEur: undefined,
  minTemperature: 0.0,
  maxTemperature: 1.0,
  maxOutputTokens: 4096,
  modelFamily: '',
  // Neue Modelle sind standardmäßig Reasoning-Modelle (Responses-API, CA-825).
  additionalConfig: { reasoningModel: true }
};

/** Formularwerte aus einem bestehenden Modell — Grundlage für Bearbeiten und Duplizieren. */
function modelToForm(model: LLMModel): CreateModelRequest {
  return {
    providerId: model.providerId,
    modelKey: model.modelKey,
    displayName: model.displayName,
    description: model.description || '',
    supportsTools: model.supportsTools,
    supportsVision: model.supportsVision,
    supportsStreaming: model.supportsStreaming,
    supportsSystemMessages: model.supportsSystemMessages,
    maxTokens: model.maxTokens || 4096,
    contextWindow: model.contextWindow || 16384,
    inputCostPerMillionTokensEur: model.inputCostPerMillionTokensEur || 1,
    outputCostPerMillionTokensEur: model.outputCostPerMillionTokensEur || 2,
    cachedInputCostPerMillionTokensEur: model.cachedInputCostPerMillionTokensEur ?? undefined,
    minTemperature: model.minTemperature || 0.0,
    maxTemperature: model.maxTemperature || 1.0,
    maxOutputTokens: model.maxOutputTokens || 4096,
    modelFamily: model.modelFamily || '',
    version: model.version || '',
    releaseDate: model.releaseDate ? new Date(model.releaseDate).toISOString().split('T')[0] : undefined,
    deprecationDate: model.deprecationDate ? new Date(model.deprecationDate).toISOString().split('T')[0] : undefined,
    additionalConfig: model.additionalConfig
  };
}

export function LLMModelsPage() {
  const [models, setModels] = useState<LLMModel[]>([]);
  const [providers, setProviders] = useState<LLMProvider[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [viewingModel, setViewingModel] = useState<LLMModel | null>(null);
  const [editingModel, setEditingModel] = useState<LLMModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | { message?: string } | null>(null);
  const [deletingModel, setDeletingModel] = useState<LLMModel | null>(null);
  const [formData, setFormData] = useState<CreateModelRequest>(emptyModelForm);

  // Load data on component mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [modelsData, providersData] = await Promise.all([
        apiService.getLlmModels({ includeInactive: true }),
        apiService.getLlmProviders(true)
      ]);
      
      setModels(modelsData);
      setProviders(providersData);
    } catch (err: any) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const filteredModels = models.filter(model =>
    model.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    model.modelKey.toLowerCase().includes(searchTerm.toLowerCase()) ||
    model.provider?.name.toLowerCase().includes(searchTerm.toLowerCase())
  );


  const handleEdit = (model: LLMModel) => {
    setEditingModel(model);
    setFormData(modelToForm(model));
    setError(null);
    setViewingModel(null);
    setIsDialogOpen(true);
  };

  /** Öffnet „Neues Modell“ vorbelegt mit den Werten eines bestehenden Modells (z.B. gleiches Modell für einen weiteren Provider). */
  const handleDuplicate = (model: LLMModel) => {
    setEditingModel(null);
    setViewingModel(null);
    setFormData({ ...modelToForm(model), displayName: `${model.displayName} (Kopie)` });
    setError(null);
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.providerId || !formData.modelKey.trim() || !formData.displayName.trim()) {
      setError('Provider, Modell-Key und Anzeigename sind Pflichtfelder.');
      return;
    }

    // Ohne Unique-Constraint auf (Provider, Modell-Key) würde ein Duplikat den Katalog-Lookup mehrdeutig machen.
    const isDuplicateKey = !editingModel && models.some(
      (m) => m.providerId === formData.providerId && m.modelKey === formData.modelKey
    );
    if (isDuplicateKey) {
      setError(`Das Modell "${formData.modelKey}" existiert für diesen Provider bereits.`);
      return;
    }

    try {
      setSaving(true);
      setError(null);

      if (editingModel) {
        // Update existing model
        const updateData: UpdateModelRequest = { ...formData };
        const updatedModel = await apiService.updateLlmModel(editingModel.id, updateData);
        setModels(models.map(m => m.id === editingModel.id ? updatedModel : m));
      } else {
        // Create new model
        const newModel = await apiService.createLlmModel(formData);
        setModels([...models, newModel]);
      }

      setIsDialogOpen(false);
      setEditingModel(null);
      setViewingModel(null);
      
      // Reset form data
      setFormData(emptyModelForm);
    } catch (err: any) {
      setError(err.message || 'Failed to save model');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (id: string) => {
    try {
      const model = models.find(m => m.id === id);
      if (!model) return;

      const newStatus = model.status === 'active' ? 'inactive' : 'active';
      const updatedModel = await apiService.updateLlmModel(id, { status: newStatus });
      setModels(models.map(m => m.id === id ? updatedModel : m));
    } catch (err: any) {
      setError(err.message || 'Failed to update model status');
    }
  };

  const handleDeleteModel = async (model: LLMModel) => {
    try {
      setSaving(true);
      await apiService.deleteLlmModel(model.id);
      setModels(models.filter(m => m.id !== model.id));
      setDeletingModel(null);
    } catch (err: any) {
      setError(err.message || 'Failed to delete model');
    } finally {
      setSaving(false);
    }
  };

  const FeatureIcon = ({ supported }: { supported: boolean }) => (
    supported ? 
      <CheckCircle className="w-4 h-4 text-green-600" /> : 
      <XCircle className="w-4 h-4 text-red-400" />
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">LLM Modelle</h1>
          <p className="text-gray-600">Verwaltung der verfügbaren Sprachmodelle</p>
        </div>
        <Button onClick={() => { setViewingModel(null); setEditingModel(null); setFormData(emptyModelForm); setError(null); setIsDialogOpen(true); }}>
          <Plus className="w-4 h-4 mr-2" />
          Neues Modell
        </Button>
      </div>

      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center space-x-2 text-red-700">
              <AlertCircle className="w-4 h-4" />
              <span>{typeof error === 'string' ? error : (error?.message || 'An error occurred')}</span>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setError(null)}
                className="ml-auto text-red-700 hover:text-red-900"
              >
                ×
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Modell Übersicht</CardTitle>
              <CardDescription>{models.length} Modelle konfiguriert</CardDescription>
            </div>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Modelle suchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-8 text-center">
              <div className="flex items-center justify-center space-x-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Lade Modelle...</span>
              </div>
            </div>
          ) : filteredModels.length === 0 ? (
            <div className="py-8 text-center text-gray-500">
              Keine Modelle gefunden
            </div>
          ) : (
            (() => {
              // Group the flat model list by provider so each provider gets its own table block
              const groups = new Map<string, { providerName: string; dataResidency?: string; models: LLMModel[] }>();
              for (const model of filteredModels) {
                const key = model.provider?.id ?? model.providerId;
                const providerName = model.provider?.displayName ?? model.provider?.name ?? 'Unbekannter Provider';
                const dataResidency = model.provider?.dataResidency;
                const existing = groups.get(key);
                if (existing) {
                  existing.models.push(model);
                } else {
                  groups.set(key, { providerName, dataResidency, models: [model] });
                }
              }
              const sortedGroups = [...groups.values()].sort((a, b) =>
                a.providerName.localeCompare(b.providerName, 'de')
              );

              return (
                <div className="space-y-6">
                  {sortedGroups.map((group) => (
                    <div key={group.providerName} className="overflow-hidden rounded-lg border border-gray-200">
                      {/* Provider group header */}
                      <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-2">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-semibold text-gray-800">{group.providerName}</span>
                          {group.dataResidency && (
                            <Badge className="text-xs font-semibold uppercase bg-blue-600 text-white border-transparent hover:bg-blue-600">
                              {group.dataResidency}
                            </Badge>
                          )}
                          <Badge variant="outline" className="text-xs">{group.models.length}</Badge>
                        </div>
                      </div>

                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-gray-200">
                              <th className="text-left py-3 px-4 font-medium text-gray-700">Modell</th>
                              <th className="text-left py-3 px-4 font-medium text-gray-700">Status</th>
                              <th className="text-left py-3 px-4 font-medium text-gray-700">Features</th>
                              <th className="text-left py-3 px-4 font-medium text-gray-700">Kosten</th>
                              <th className="text-left py-3 px-4 font-medium text-gray-700">Aktionen</th>
                            </tr>
                          </thead>
                          <tbody>
                            {group.models.map((model) => (
                              <tr key={model.id} className="border-b border-gray-100 hover:bg-gray-50">
                                <td className="py-3 px-4">
                                  <div>
                                    <div className="font-medium text-gray-900">{model.displayName}</div>
                                    <div className="text-sm text-gray-600">{model.modelKey}</div>
                                    <div className="text-xs text-gray-500">{model.modelFamily}</div>
                                  </div>
                                </td>
                                <td className="py-3 px-4">
                                  <Badge variant={model.status === 'active' ? 'default' : 'secondary'}>
                                    {model.status === 'active' ? 'Aktiv' : model.status === 'inactive' ? 'Inaktiv' : model.status}
                                  </Badge>
                                </td>
                                <td className="py-3 px-4">
                                  <div className="flex space-x-1">
                                    <div title="Tools" className="flex items-center">
                                      <FeatureIcon supported={model.supportsTools} />
                                    </div>
                                    <div title="Vision" className="flex items-center">
                                      <FeatureIcon supported={model.supportsVision} />
                                    </div>
                                    <div title="Streaming" className="flex items-center">
                                      <FeatureIcon supported={model.supportsStreaming} />
                                    </div>
                                  </div>
                                </td>
                                <td className="py-3 px-4">
                                  <div className="text-sm">
                                    <div>€{model.inputCostPerMillionTokensEur} / €{model.outputCostPerMillionTokensEur}</div>
                                    <div className="text-xs text-gray-500">Input / Output pro 1 Mio. Tokens (EUR)</div>
                                  </div>
                                </td>
                                <td className="py-3 px-4">
                                  <div className="flex items-center space-x-2">
                                    <Switch
                                      checked={model.status === 'active'}
                                      onCheckedChange={() => toggleActive(model.id)}
                                    />
                                    <Button variant="ghost" size="sm" onClick={() => handleEdit(model)}>
                                      <Edit className="w-4 h-4" />
                                    </Button>
                                    <Button variant="ghost" size="sm" title="Duplizieren" onClick={() => handleDuplicate(model)}>
                                      <Copy className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => setDeletingModel(model)}
                                      className="text-red-600 hover:text-red-800 hover:bg-red-50"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()
          )}
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {viewingModel ? 'Modell Details' :
               editingModel ? 'Modell bearbeiten' : 'Neues Modell erstellen'}
            </DialogTitle>
            <DialogDescription>
              {viewingModel ? 'Detaillierte Informationen über das Modell' :
               'Konfigurieren Sie die Einstellungen für das LLM Modell'}
            </DialogDescription>
          </DialogHeader>

          {viewingModel ? (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Grundinformationen</h4>
                    <div className="space-y-2 text-sm">
                      <div><strong>Anzeigename:</strong> {viewingModel.displayName}</div>
                      <div><strong>Key:</strong> {viewingModel.modelKey}</div>
                      <div><strong>Provider:</strong> {viewingModel.provider?.displayName || 'N/A'}</div>
                      <div><strong>Familie:</strong> {viewingModel.modelFamily}</div>
                      <div><strong>Status:</strong> {viewingModel.status === 'active' ? 'Aktiv' : 'Inaktiv'}</div>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Beschreibung</h4>
                    <p className="text-sm text-gray-600">{viewingModel.description}</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Technische Details</h4>
                    <div className="space-y-2 text-sm">
                      <div><strong>Max Tokens:</strong> {viewingModel.maxTokens?.toLocaleString() || 'N/A'}</div>
                      <div><strong>Context Window:</strong> {viewingModel.contextWindow?.toLocaleString() || 'N/A'}</div>
                      <div><strong>Max Output Tokens:</strong> {viewingModel.maxOutputTokens?.toLocaleString() || 'N/A'}</div>
                      <div><strong>Temperature:</strong> {viewingModel.minTemperature || 'N/A'} - {viewingModel.maxTemperature || 'N/A'}</div>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Kosten</h4>
                    <div className="space-y-2 text-sm">
                      <div><strong>Input:</strong> €{viewingModel.inputCostPerMillionTokensEur} / 1 Mio. Tokens</div>
                      <div><strong>Cached Input:</strong> {viewingModel.cachedInputCostPerMillionTokensEur != null ? `€${viewingModel.cachedInputCostPerMillionTokensEur} / 1 Mio. Tokens` : 'N/A (voller Input-Preis)'}</div>
                      <div><strong>Output:</strong> €{viewingModel.outputCostPerMillionTokensEur} / 1 Mio. Tokens</div>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Unterstützte Features</h4>
                <div className="grid grid-cols-2 gap-2">
                  <div className="flex items-center space-x-2">
                    <FeatureIcon supported={viewingModel.supportsTools} />
                    <span className="text-sm">Tools</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <FeatureIcon supported={viewingModel.supportsVision} />
                    <span className="text-sm">Vision</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <FeatureIcon supported={viewingModel.supportsStreaming} />
                    <span className="text-sm">Streaming</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <FeatureIcon supported={viewingModel.supportsSystemMessages} />
                    <span className="text-sm">System Messages</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <FeatureIcon supported={viewingModel.additionalConfig?.reasoningModel ?? false} />
                    <span className="text-sm">Reasoning (Responses-API)</span>
                  </div>
                </div>
              </div>
              <div>
                <ParameterPreview modelId={viewingModel.id} />
              </div>
              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Schließen
                </Button>
                <Button onClick={() => handleEdit(viewingModel)}>
                  <Edit className="w-4 h-4 mr-2" />
                  Bearbeiten
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="provider">Provider</Label>
                  <Select value={formData.providerId} onValueChange={(value) => setFormData({...formData, providerId: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Provider wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {providers.map((provider) => (
                        <SelectItem key={provider.id} value={provider.id}>
                          {provider.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="modelKey">Model Key</Label>
                  <Input
                    id="modelKey"
                    value={formData.modelKey}
                    onChange={(e) => setFormData({...formData, modelKey: e.target.value})}
                    placeholder="z.B. gpt-4-turbo"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="displayName">Anzeigename</Label>
                  <Input
                    id="displayName"
                    value={formData.displayName}
                    onChange={(e) => setFormData({...formData, displayName: e.target.value})}
                    placeholder="z.B. GPT-4 Turbo"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="modelFamily">Modellfamilie</Label>
                  <Input
                    id="modelFamily"
                    value={formData.modelFamily}
                    onChange={(e) => setFormData({...formData, modelFamily: e.target.value})}
                    placeholder="z.B. GPT-4"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Beschreibung</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Beschreibung des Modells..."
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="supportsTools"
                    checked={formData.supportsTools}
                    onCheckedChange={(checked) => setFormData({...formData, supportsTools: checked})}
                  />
                  <Label htmlFor="supportsTools">Tools</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="supportsVision"
                    checked={formData.supportsVision}
                    onCheckedChange={(checked) => setFormData({...formData, supportsVision: checked})}
                  />
                  <Label htmlFor="supportsVision">Vision</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="supportsStreaming"
                    checked={formData.supportsStreaming}
                    onCheckedChange={(checked) => setFormData({...formData, supportsStreaming: checked})}
                  />
                  <Label htmlFor="supportsStreaming">Streaming</Label>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="supportsSystemMessages"
                  checked={formData.supportsSystemMessages}
                  onCheckedChange={(checked) => setFormData({...formData, supportsSystemMessages: checked})}
                />
                <Label htmlFor="supportsSystemMessages">System Nachrichten</Label>
              </div>
              <div className="space-y-1">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="reasoningModel"
                    checked={formData.additionalConfig?.reasoningModel ?? false}
                    onCheckedChange={(checked) => setFormData({
                      ...formData,
                      additionalConfig: { ...formData.additionalConfig, reasoningModel: checked },
                    })}
                  />
                  <Label htmlFor="reasoningModel">Reasoning-Modell</Label>
                </div>
                <p className="text-xs text-gray-500">
                  Nur OpenAI-Treiber (OpenAI, Azure, OpenAI-kompatibel): Anfragen laufen über die Responses-API — reasoning_effort ist dann auch mit Tools nutzbar, Sampling-Parameter (temperature, top_p) entfallen.
                </p>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="maxTokens">Max Tokens</Label>
                  <Input
                    id="maxTokens"
                    type="number"
                    value={formData.maxTokens}
                    onChange={(e) => setFormData({...formData, maxTokens: parseInt(e.target.value) || 0})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contextWindow">Context Window</Label>
                  <Input
                    id="contextWindow"
                    type="number"
                    value={formData.contextWindow}
                    onChange={(e) => setFormData({...formData, contextWindow: parseInt(e.target.value) || 0})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="maxOutputTokens">Max Output Tokens</Label>
                  <Input
                    id="maxOutputTokens"
                    type="number"
                    value={formData.maxOutputTokens}
                    onChange={(e) => setFormData({...formData, maxOutputTokens: parseInt(e.target.value) || 0})}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="inputCostPerMillionTokensEur">Input Kosten / 1 Mio. (EUR)</Label>
                  <Input
                    id="inputCostPerMillionTokensEur"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.inputCostPerMillionTokensEur}
                    onChange={(e) => setFormData({...formData, inputCostPerMillionTokensEur: parseFloat(e.target.value) || 0})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="outputCostPerMillionTokensEur">Output Kosten / 1 Mio. (EUR)</Label>
                  <Input
                    id="outputCostPerMillionTokensEur"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.outputCostPerMillionTokensEur}
                    onChange={(e) => setFormData({...formData, outputCostPerMillionTokensEur: parseFloat(e.target.value) || 0})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cachedInputCostPerMillionTokensEur">Cached Input Kosten / 1 Mio. (EUR)</Label>
                  <Input
                    id="cachedInputCostPerMillionTokensEur"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="leer = voller Input-Preis"
                    value={formData.cachedInputCostPerMillionTokensEur ?? ''}
                    onChange={(e) => setFormData({...formData, cachedInputCostPerMillionTokensEur: e.target.value === '' ? undefined : parseFloat(e.target.value) || 0})}
                  />
                </div>
              </div>

              <div className="border-t pt-4">
                <Tabs defaultValue="supported" className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="supported">Erweiterte Parameter</TabsTrigger>
                    <TabsTrigger value="unsupported">Nicht unterstützte Parameter</TabsTrigger>
                  </TabsList>
                  <TabsContent value="supported" className="space-y-4">
                    <ParameterEditor
                      parameters={formData.additionalConfig?.supportedParameters || []}
                      onChange={(parameters) => setFormData({
                        ...formData,
                        additionalConfig: {
                          ...formData.additionalConfig,
                          supportedParameters: parameters
                        }
                      })}
                      title="Modell-spezifische Parameter"
                      description="Erweitern oder überschreiben Sie Provider-Parameter für dieses Modell"
                    />
                  </TabsContent>
                  <TabsContent value="unsupported" className="space-y-4">
                    <Card>
                      <CardHeader>
                        <CardTitle>Nicht unterstützte Parameter</CardTitle>
                        <CardDescription>
                          Blacklist von Provider-Parametern, die dieses Modell nicht unterstützt
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          <Label htmlFor="unsupportedParameters">Parameter Namen (kommagetrennt)</Label>
                          <Input
                            id="unsupportedParameters"
                            value={formData.additionalConfig?.unsupportedParameters?.join(', ') || ''}
                            onChange={(e) => {
                              const params = e.target.value.split(',').map(p => p.trim()).filter(Boolean);
                              setFormData({
                                ...formData,
                                additionalConfig: {
                                  ...formData.additionalConfig,
                                  unsupportedParameters: params
                                }
                              });
                            }}
                            placeholder="z.B. top_k, frequency_penalty"
                          />
                          <p className="text-sm text-gray-500">
                            Diese Parameter werden aus der Provider-Konfiguration ausgeschlossen
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>
                </Tabs>
              </div>
              {error && (
                <p className="text-sm text-red-600">
                  {typeof error === 'string' ? error : (error?.message || 'An error occurred')}
                </p>
              )}
              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Abbrechen
                </Button>
                <Button onClick={handleSave} disabled={saving}>
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {editingModel ? 'Speichern' : 'Erstellen'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deletingModel} onOpenChange={() => setDeletingModel(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <span>Modell löschen</span>
            </DialogTitle>
            <DialogDescription>
              Sind Sie sicher, dass Sie das Modell "{deletingModel?.displayName}" löschen möchten? 
              Das Modell wird auf "inaktiv" gesetzt und kann nicht mehr verwendet werden.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end space-x-2 mt-6">
            <Button 
              variant="outline" 
              onClick={() => setDeletingModel(null)}
              disabled={saving}
            >
              Abbrechen
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => deletingModel && handleDeleteModel(deletingModel)}
              disabled={saving}
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Löschen
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}